import React from 'react';
import { Modal, Button, Spin, message, Alert, Upload, Card, Tag, Input } from 'antd';
import { CheckCircleFilled, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Internship } from '../../services/types/internship.types';
import { ApplicationValidationService, ValidationResult, ValidationResponse, SupportingDocument } from '../../services/ApplicationValidationService';
import { validateFile, getAllowedFileTypesDisplay, formatFileSize } from '../../config/fileUpload';
import { FileUploadService, FileUploadResponse } from '../../services/FileUploadService';
import ErrorDialog from './ErrorDialog';
import { isStudentUser } from '../../utils/auth';
import './ApplyInternshipModal.css';

// Success Modal Component
const SuccessModal: React.FC<{
    visible: boolean;
    onClose: () => void;
    onSubmit: () => void;
}> = ({ visible, onClose, onSubmit }) => (
    <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        title={
            <div className="success-modal-title-container">
                <CheckCircleFilled className="success-modal-icon" />
                <div className="success-modal-title-text">Application Submitted Successfully!</div>
            </div>
        }
        centered
        width={500}
        closable={false}
    >
        <div className="success-modal-content-container">
            <p className="success-modal-message">
                You have successfully submitted your application for this internship. 
                Our team will review your application and get back to you soon.
            </p>
            <div className="success-modal-actions-container">
                <Button onClick={onClose} size="large">
                    Close
                </Button>
                <Button type="primary" onClick={onSubmit} size="large">
                    View Applications
                </Button>
            </div>
        </div>
    </Modal>
);

interface ApplyInternshipModalProps {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (applicationId?: string) => void;
    submitting?: boolean;
    internship: Internship | null;
}

const ApplyInternshipModal: React.FC<ApplyInternshipModalProps> = ({ 
    visible, 
    onCancel, 
    onSubmit, 
    submitting = false,
    internship 
}) => {
    const navigate = useNavigate();
    const errorAlertsRef = React.useRef<HTMLDivElement>(null);

    const [validationResults, setValidationResults] = React.useState<{
        eligibility: ValidationResult;
        skills: ValidationResult;
    }>({
        eligibility: { status: 'loading', message: 'Validating eligibility...' },
        skills: { status: 'pending', message: 'Pending skills validation...' }
    });

    const [supportingDocuments, setSupportingDocuments] = React.useState<SupportingDocument[]>([]);
    const [profileMatch, setProfileMatch] = React.useState<string>('');
    const [uploadedFiles, setUploadedFiles] = React.useState<{[key: number]: File}>({});
    const [uploadedFileResponses, setUploadedFileResponses] = React.useState<{[key: number]: FileUploadResponse}>({});
    const [uploadingFiles, setUploadingFiles] = React.useState<{[key: number]: boolean}>({});
    const [applicationNotes, setApplicationNotes] = React.useState<string>('');
    const [showSuccessModal, setShowSuccessModal] = React.useState<boolean>(false);
    const [isValidating, setIsValidating] = React.useState(false);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [showPageLoader, setShowPageLoader] = React.useState(false);
    const [showErrorDialog, setShowErrorDialog] = React.useState(false);
    const [serverError, setServerError] = React.useState<string | null>(null);
    const [submissionError, setSubmissionError] = React.useState<string | null>(null);
    const [generalError, setGeneralError] = React.useState<string | null>(null);
    const [forceUpdate, setForceUpdate] = React.useState(0);
    const [missingCoursesExpanded, setMissingCoursesExpanded] = React.useState(false);
    const [missingSkillsExpanded, setMissingSkillsExpanded] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submittedApplicationId, setSubmittedApplicationId] = React.useState<string | undefined>(undefined);

    const scrollToTop = React.useCallback(() => {
        setTimeout(() => {
            const modalBody = document.querySelector('.apply-modal-content .ant-modal-body');
            if (modalBody) {
                modalBody.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 100);
    }, []);

    const scrollToError = React.useCallback(() => {
        console.log('🎯 [ApplyInternshipModal] scrollToError called, errorAlertsRef.current:', !!errorAlertsRef.current);
        setTimeout(() => {
            if (errorAlertsRef.current) {
                console.log('🎯 [ApplyInternshipModal] Scrolling to error alerts container');
                // Scroll to error with a small offset to ensure it's fully visible
                errorAlertsRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
                
                // Additional scroll adjustment to ensure error is not hidden behind modal header
                const modalBody = document.querySelector('.apply-modal-content .ant-modal-body');
                if (modalBody) {
                    setTimeout(() => {
                        modalBody.scrollTop = Math.max(0, modalBody.scrollTop - 20);
                        console.log('🎯 [ApplyInternshipModal] Applied additional scroll offset');
                    }, 300);
                }
            } else {
                console.log('🎯 [ApplyInternshipModal] Error ref not available, falling back to scrollToTop');
                // Fallback to scrolling to top if error ref is not available
                scrollToTop();
            }
        }, 100);
    }, [scrollToTop]);

    const clearAllErrors = () => {
        setGeneralError(null);
        setSubmissionError(null);
        setValidationError(null);
        setServerError(null);
        setApplicationNotes(''); // Clear application notes when clearing errors
    };

    // Auto-scroll to error messages when they occur
    React.useEffect(() => {
        if (submissionError || generalError || validationError) {
            console.log('🔄 [ApplyInternshipModal] Auto-scrolling to error message:', { submissionError, generalError, validationError });
            scrollToError();
        }
    }, [submissionError, generalError, validationError, scrollToError]);

    const runValidation = React.useCallback(async () => {
        if (!internship) {
            setValidationError('No internship selected for validation');
            return;
        }

        setIsValidating(true);
        setValidationError(null);
        setServerError(null);
        
        try {
            console.log('🚀 [ApplyInternshipModal] Starting validation for internship:', internship.id);
            
            // Run validation using the ApplicationValidationService
            const validationResponse: ValidationResponse = await ApplicationValidationService.runValidation(internship);
            
            // Log the validation response for debugging
            console.log('📊 [ApplyInternshipModal] Validation response received:', validationResponse);
            console.log('📊 [ApplyInternshipModal] Eligibility details:', validationResponse.eligibility.details);
            console.log('📊 [ApplyInternshipModal] Skills details:', validationResponse.skills.details);
            
            // Update state with validation results
            setValidationResults({
                eligibility: validationResponse.eligibility,
                skills: validationResponse.skills
            });
            
            setSupportingDocuments(validationResponse.supportingDocuments || []);
            setProfileMatch(validationResponse.profileMatch || '');
            
            console.log('✅ [ApplyInternshipModal] Validation completed successfully');
            
        } catch (error: any) {
            console.error('❌ [ApplyInternshipModal] Validation failed:', error);
            console.log('📝 [ApplyInternshipModal] Error structure:', {
                message: error.message,
                response: error.response,
                data: error.data,
                status: error.status,
                type: typeof error,
                keys: Object.keys(error || {})
            });
            
            // Check if it's a server error or global exception
            if (error.message?.includes('Network') || 
                error.message?.includes('500') || 
                error.message?.includes('Server') ||
                error.message?.includes('Connection') ||
                error.status >= 500) {
                // Show error dialog for server errors
                const errorMessage = formatErrorMessage(error);
                setServerError(errorMessage);
                setShowErrorDialog(true);
            } else {
                // Show validation error in the modal - this includes backend API errors
                const errorMessage = formatErrorMessage(error);
                console.log('📝 [ApplyInternshipModal] Setting validation error:', errorMessage);
                setValidationError(errorMessage);
            }
            
            // Set error states for both validations
            setValidationResults({
                eligibility: { status: 'error', message: 'Failed to validate eligibility' },
                skills: { status: 'error', message: 'Failed to validate skills' }
            });
        } finally {
            setIsValidating(false);
        }
    }, [internship]);

    const handleFileUpload = async (documentId: number, file: File) => {
        // Clear any previous errors when starting a new upload
        clearAllErrors();
        
        // Validate file using global configuration
        const validation = validateFile(file);
        
        if (!validation.isValid) {
            setGeneralError(validation.error || 'File validation failed');
            setForceUpdate(prev => prev + 1);
            return false; // Prevent upload
        }
        
        // Set uploading state
        setUploadingFiles(prev => ({
            ...prev,
            [documentId]: true
        }));

        try {
            // Get supporting document details
            const document = supportingDocuments.find(doc => doc.internshipDocumentId === documentId);
            if (!document) {
                throw new Error('Document configuration not found');
            }

            // Upload file to backend
            const uploadResponse = await FileUploadService.uploadFile(file, {
                uploadedFor: 'INTERNSHIP_APPLICATION',
                fileType: document.documentTypeCode || 'SUPPORTING_DOCUMENT',
                referenceId: internship?.id?.toString(),
                metadata: JSON.stringify({
                    documentId: documentId,
                    documentName: document.documentNameName,
                    documentType: document.documentTypeName,
                    isMandatory: document.isMandatory
                })
            });

            // Store both file and response
            setUploadedFiles(prev => ({
                ...prev,
                [documentId]: file
            }));

            setUploadedFileResponses(prev => ({
                ...prev,
                [documentId]: uploadResponse
            }));
            
            // Clear any remaining errors after successful upload
            clearAllErrors();
            
            message.success(`File "${file.name}" uploaded successfully`);
            
        } catch (error: any) {
            console.error('Error uploading file:', error);
            const errorMessage = formatErrorMessage(error);
            setGeneralError(errorMessage);
            setForceUpdate(prev => prev + 1);
        } finally {
            // Clear uploading state
            setUploadingFiles(prev => ({
                ...prev,
                [documentId]: false
            }));
        }
        
        return false; // Prevent default upload behavior
    };

    const handleFileRemove = async (documentId: number) => {
        // Clear any previous errors when removing a file
        clearAllErrors();
        
        try {
            // If we have an uploaded file response, delete the file from backend
            const fileResponse = uploadedFileResponses[documentId];
            if (fileResponse) {
                await FileUploadService.deleteFile(fileResponse.fileUploadId);
                message.success('File removed successfully');
            }
        } catch (error: any) {
            console.error('Error removing file from backend:', error);
            const errorMessage = formatErrorMessage(error);
            message.warning(`File removed locally but may still exist on server: ${errorMessage}`);
        }

        // Remove from local state
        setUploadedFiles(prev => {
            const newFiles = { ...prev };
            delete newFiles[documentId];
            return newFiles;
        });

        setUploadedFileResponses(prev => {
            const newResponses = { ...prev };
            delete newResponses[documentId];
            return newResponses;
        });
    };

    const canSubmit = () => {
        // Check if profile matches
        if (profileMatch !== 'Success') return false;
        
        // Check if any files are currently uploading
        const isAnyFileUploading = Object.values(uploadingFiles).some(uploading => uploading);
        if (isAnyFileUploading) return false;
        
        // Check if all mandatory documents are uploaded
        const mandatoryDocuments = supportingDocuments.filter(doc => doc.isMandatory);
        const uploadedMandatoryDocs = mandatoryDocuments.filter(doc => uploadedFiles[doc.internshipDocumentId]);
        
        return uploadedMandatoryDocs.length >= mandatoryDocuments.length;
    };

    const canShowRequirementsMismatch = () => {
        // Only show requirements mismatch if profile doesn't match
        return profileMatch !== 'Success';
    };

    const handleSubmit = async () => {
        if (!isStudentUser()) {
            setGeneralError('Only students can apply for internships.');
            message.warning('Only students can apply for internships');
            return;
        }
        if (!internship) {
            setGeneralError('No internship selected');
            setForceUpdate(prev => prev + 1);
            return;
        }

        if (!canSubmit()) {
            setGeneralError('Please upload all mandatory documents before submitting');
            setForceUpdate(prev => prev + 1);
            return;
        }

        // Clear any previous submission errors
        setSubmissionError(null);

        try {
            setIsSubmitting(true);
            console.log('📝 [ApplyInternshipModal] Submitting application for internship:', internship.id);
            
            // Prepare uploaded files data for submission
            // Backend expects just a list of file upload IDs (strings)
            const uploadedDocuments = Object.values(uploadedFileResponses).map(fileResponse => 
                fileResponse.fileUploadId.toString()
            );

            console.log('📁 [ApplyInternshipModal] Uploaded documents:', uploadedDocuments);
            
            // Submit application using the ApplicationValidationService with uploaded files
            const response = await ApplicationValidationService.submitApplication(
                internship.id,
                uploadedDocuments,
                applicationNotes,
                internship.internshipCode
            );
            
            console.log('📝 [ApplyInternshipModal] Received response from submitApplication:', response);
            console.log('📝 [ApplyInternshipModal] Response status:', response.status);
            console.log('📝 [ApplyInternshipModal] Response message:', response.message);
            
            if (response.status === 'success') {
                message.success('Application submitted successfully!');
                setSubmittedApplicationId(response.data?.applicationId);
                setShowSuccessModal(true); // Show success modal
                // Close the main modal immediately when showing success modal
                onCancel();
            } else {
                // Display the error message from the API response
                const errorMessage = response.message || 'Failed to submit application';
                console.error('❌ [ApplyInternshipModal] API submission failed:', response);
                console.log('📝 [ApplyInternshipModal] Setting submission error:', errorMessage);
                setSubmissionError(errorMessage);
                setForceUpdate(prev => prev + 1); // Force re-render
            }
        } catch (error: any) {
            console.error('❌ [ApplyInternshipModal] Error submitting application:', error);
            
            // Extract error message from various error formats
            let errorMessage = 'Failed to submit application';
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }
            
            console.log('📝 [ApplyInternshipModal] Setting submission error from catch block:', errorMessage);
            setSubmissionError(errorMessage);
            setForceUpdate(prev => prev + 1); // Force re-render
        } finally {
            setIsSubmitting(false);
        }
    };

    // Track error state changes for debugging
    React.useEffect(() => {
        console.log('📝 [ApplyInternshipModal] Error state changed:', { submissionError, generalError, validationError, forceUpdate });
    }, [submissionError, generalError, validationError, forceUpdate]);

    React.useEffect(() => {
        if (visible && internship) {
            // Show page loader first
            setShowPageLoader(true);
            
            // Reset validation state when modal becomes visible
            setValidationResults({
                eligibility: { status: 'loading', message: 'Validating eligibility...' },
                skills: { status: 'pending', message: 'Pending skills validation...' }
            });
            setSupportingDocuments([]);
            setProfileMatch('');
            setUploadedFiles({});
            setUploadedFileResponses({});
            setUploadingFiles({});
            console.log('📝 [ApplyInternshipModal] Resetting error states in useEffect');
            clearAllErrors();
            setShowErrorDialog(false);
            setForceUpdate(0); // Reset force update counter
            setMissingCoursesExpanded(false); // Reset expand state
            setMissingSkillsExpanded(false); // Reset expand state
            
            // Hide page loader and start validation after a short delay
            setTimeout(() => {
                setShowPageLoader(false);
                runValidation();
            }, 500);
        }
    }, [visible, internship, runValidation]);

    // Clear errors when modal becomes visible
    React.useEffect(() => {
        if (visible) {
            // Clear all error states when modal opens
            clearAllErrors();
        }
    }, [visible]);

    const formatErrorMessage = (error: any): string => {
        if (typeof error === 'string') {
            return error;
        }
        
        if (error?.message) {
            return error.message;
        }
        
        if (error?.response?.data?.message) {
            return error.response.data.message;
        }
        
        if (error?.response?.data?.error) {
            return error.response.data.error;
        }
        
        return 'An unexpected error occurred. Please try again.';
    };

    const handleUnexpectedError = (error: any, context: string) => {
        console.error(`❌ [ApplyInternshipModal] Unexpected error in ${context}:`, error);
        const errorMessage = formatErrorMessage(error);
        setGeneralError(`Error in ${context}: ${errorMessage}`);
    };

    // Show page loader
    if (showPageLoader) {
        return (
            <Modal
                open={visible}
                onCancel={onCancel}
                footer={null}
                title="Loading..."
                centered
                width={400}
                closable={false}
            >
                <div className="page-loader-container">
                    <Spin size="large" />
                    <div className="page-loader-spinner">
                        Preparing application validation...
                    </div>
                </div>
            </Modal>
        );
    }

    // Show error dialog only for server errors
    if (showErrorDialog && serverError) {
        return (
            <ErrorDialog
                visible={visible}
                title="Server Error"
                message={serverError}
                onClose={onCancel}
                showRetry={false}
            />
        );
    }

    try {
        return (
            <>
                <Modal
                open={visible}
                onCancel={onCancel}
                footer={null}
                title={
                    <div className="apply-modal-header">
                        <div className="apply-modal-title">
                            Apply for Internship
                        </div>
                        <div className="apply-modal-subtitle">
                            Welcome! Let's get you started with your application
                        </div>
                    </div>
                }
                centered
                width={700}
                className="apply-modal-content apply-modal-body"
            >
                {/* Error Alerts Container - Show at the top for maximum visibility */}
                {(submissionError || generalError || validationError) && (
                    <div className="error-alerts-container" ref={errorAlertsRef}>
                                       
                        {/* Show submission error alert if there's an error from API submission */}
                        {submissionError && (
                            <Alert
                                message="Application Submission Failed"
                                description={submissionError}
                                type="error"
                                showIcon
                                className="submission-error-alert"
                                closable
                                onClose={() => setSubmissionError(null)}
                            />
                        )}

                        {/* Show general error alert if there's any other type of error */}
                        {generalError && (
                            <Alert
                                message="Error Occurred"
                                description={generalError}
                                type="error"
                                showIcon
                                className="general-error-alert"
                                closable
                                onClose={() => setGeneralError(null)}
                            />
                        )}

                        {/* Show validation error alert if there's a validation error */}
                        {validationError && (
                            <Alert
                                description={validationError}
                                type="error"
                                showIcon
                                onClose={() => setValidationError(null)}
                            />
                        )}
                    </div>
                )}


                {/* Validation Steps - Only show when no validation error and profile doesn't match */}
                

                {/* Requirements Mismatch Message - Only show when profile doesn't match */}
                {canShowRequirementsMismatch() && !isValidating && !validationError && (
                    <div className="requirements-mismatch">
                        <div className="requirements-mismatch-header">
                            <div className="requirements-mismatch-icon">⚠️</div>
                            <div className="requirements-mismatch-title">
                                Requirements Don't Match
                            </div>
                        </div>
                        <div className="requirements-mismatch-summary">
                            {/* Education Requirements Row */}
                            <div className="summary-item">
                                <span className="summary-label">Education Requirements:</span>
                                <div className="summary-item-actions">
                                    {validationResults.eligibility.status !== 'success' && (
                                        <Button 
                                            type="text" 
                                            size="small"
                                            onClick={() => setMissingCoursesExpanded(!missingCoursesExpanded)}
                                            className="expand-collapse-btn"
                                        >
                                            {missingCoursesExpanded ? 'Collapse Details' : 'View Details'}
                                            <span className={`expand-icon ${missingCoursesExpanded ? 'expanded' : ''}`}>
                                                ▼
                                            </span>
                                        </Button>
                                    )}
                                    <span className={`summary-status ${validationResults.eligibility.status === 'success' ? 'met' : 'missing'}`}>
                                        {validationResults.eligibility.status === 'success' ? '✅ Met' : '❌ NOT MET'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Missing Courses List - Display below when expanded */}
                            {validationResults.eligibility.status !== 'success' && 
                                validationResults.eligibility?.details?.missingCourses && 
                                validationResults.eligibility.details.missingCourses.length > 0 && (
                                <div className={`missing-courses-content ${missingCoursesExpanded ? 'expanded' : ''}`}>
                                    <div className="missing-courses-list">
                                        {validationResults.eligibility.details.missingCourses.map((course: any, index: number) => (
                                            <div key={index} className="missing-course-card">
                                                <div className="course-card-header">
                                                    <strong>Course Requirement {index + 1}</strong>
                                                </div>
                                                <div className="course-card-details">
                                                    {course.courseLevelName && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label">Level:</span>
                                                            <span className="detail-value">{course.courseLevelName}</span>
                                                        </div>
                                                    )}
                                                    {course.courseName && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label">Course:</span>
                                                            <span className="detail-value">{course.courseName}</span>
                                                        </div>
                                                    )}
                                                    {course.specializationName && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label">Specialization:</span>
                                                            <span className="detail-value">{course.specializationName}</span>
                                                        </div>
                                                    )}
                                                    {course.gradeName && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label">Grade:</span>
                                                            <span className="detail-value">{course.gradeName}</span>
                                                        </div>
                                                    )}
                                                    {course.graduationYear && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label">{course.studentStatus} on :</span>
                                                            <span className="detail-value">{course.graduationYear}</span>
                                                        </div>
                                                    )}
                                                    {course.studentStatusName && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label"> Student Status:</span>
                                                            <span className="detail-value">{course.studentStatusName}</span>
                                                        </div>
                                                    )}
                                                    {course.completionYearName && (
                                                        <div className="course-detail-row">
                                                            <span className="detail-label">Completion Year:</span>
                                                            <span className="detail-value">{course.completionYearName}</span>
                                                        </div>
                                                    )}
                                                    
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Skills Requirements Row */}
                            <div className="summary-item">
                                <span className="summary-label">Skills Requirements:</span>
                                <div className="summary-item-actions">
                                    {validationResults.skills.status !== 'success' && (
                                        <Button 
                                            type="text" 
                                            size="small"
                                            onClick={() => setMissingSkillsExpanded(!missingSkillsExpanded)}
                                            className="expand-collapse-btn"
                                        >
                                            {missingSkillsExpanded ? 'Collapse Details' : 'View Details'}
                                            <span className={`expand-icon ${missingSkillsExpanded ? 'expanded' : ''}`}>
                                                ▼
                                            </span>
                                        </Button>
                                    )}
                                    <span className={`summary-status ${validationResults.skills.status === 'success' ? 'met' : 'missing'}`}>
                                        {validationResults.skills.status === 'success' ? '✅ Met' : '❌ NOT MET'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Missing Skills List - Display below when expanded */}
                            {validationResults.skills.status !== 'success' && 
                                validationResults.skills?.details?.missingSkills && 
                                validationResults.skills.details.missingSkills.length > 0 && (
                                <div className={`missing-skills-content ${missingSkillsExpanded ? 'expanded' : ''}`}>
                                    <div className="missing-skills-list">
                                        {validationResults.skills.details.missingSkills.map((skill: any, index: number) => (
                                            <div key={index} className="missing-skill-card">
                                                <div className="skill-card-header">
                                                    <strong>Skill Requirement {index + 1}</strong>
                                                </div>
                                                <div className="skill-card-details">
                                                    {skill.skillName && (
                                                        <div className="skill-detail-row">
                                                            <span className="detail-label">SKILL:</span>
                                                            <span className="detail-value">{skill.skillName}</span>
                                                        </div>
                                                    )}
                                                    {skill.proficiencyLevelName && (
                                                        <div className="skill-detail-row">
                                                            <span className="detail-label">LEVEL:</span>
                                                            <span className="detail-value">{skill.proficiencyLevelName}</span>
                                                        </div>
                                                    )}
                                                    {skill.experienceYears && (
                                                        <div className="skill-detail-row">
                                                            <span className="detail-label"> Experience Years:</span>
                                                            <span className="detail-value">{skill.experienceYears}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        
                        
                        
                        
                        <div className="requirements-mismatch-message">
                            Your profile doesn't meet the internship requirements. 
                            Please update your education details and skills to improve your match.
                        </div>
                        <div className="requirements-mismatch-actions">
                            <Button 
                                type="primary" 
                                onClick={() => navigate('/student/profile')}
                                size="small"
                            >
                                Update Profile
                            </Button>
                        </div>
                    </div>
                )}

                {/* Supporting Documents Section - Only show when profile matches */}
                {!validationError && profileMatch === 'Success' && supportingDocuments.length > 0 && (
                    <div className="supporting-documents-section">
                        <div className="supporting-documents-title">
                            Supporting Documents
                        </div>
                        <div className="supporting-documents-description">
                            It is necessary to upload required supporting documents for your application. 
                            Mandatory documents must be uploaded before you can submit your application.
                            <br />
                            <strong>Allowed file types:</strong> {getAllowedFileTypesDisplay()}
                            <br />
                            <strong>Maximum file size:</strong> {formatFileSize(1024 * 1024)}
                        </div>
                        <div className="supporting-documents-list">
                            {supportingDocuments.map((document) => (
                                <Card 
                                    key={document.internshipDocumentId} 
                                    size="small"
                                    className={`document-card ${uploadedFiles[document.internshipDocumentId] ? 'uploaded' : ''}`}
                                >
                                    <div className="document-card-content">
                                        <div className="document-info">
                                            <div className="document-name">
                                                {document.documentNameName}
                                            </div>
                                            <div className="document-type">
                                                {document.documentTypeName}
                                            </div>
                                        </div>
                                        <div className="document-actions">
                                            {document.isMandatory && (
                                                <Tag color="red">Mandatory</Tag>
                                            )}
                                            {!document.isMandatory && (
                                                <Tag color="blue">Optional</Tag>
                                            )}
                                            <Upload
                                                beforeUpload={(file) => handleFileUpload(document.internshipDocumentId, file)}
                                                onRemove={() => handleFileRemove(document.internshipDocumentId)}
                                                fileList={uploadedFiles[document.internshipDocumentId] ? [{
                                                    uid: document.internshipDocumentId.toString(),
                                                    name: uploadedFiles[document.internshipDocumentId].name,
                                                    status: uploadingFiles[document.internshipDocumentId] ? 'uploading' : 'done'
                                                }] : []}
                                                maxCount={1}
                                                accept=".xlsx,.xls,.doc,.docx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png"
                                            >
                                                <Button 
                                                    icon={<UploadOutlined />} 
                                                    size="small"
                                                    loading={uploadingFiles[document.internshipDocumentId]}
                                                    disabled={uploadingFiles[document.internshipDocumentId]}
                                                >
                                                    {uploadingFiles[document.internshipDocumentId] 
                                                        ? 'Uploading...' 
                                                        : uploadedFiles[document.internshipDocumentId] 
                                                            ? 'Change' 
                                                            : 'Upload'
                                                    }
                                                </Button>
                                            </Upload>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* Application Notes Section - Only show when profile matches */}
                {!validationError && profileMatch === 'Success' && (
                    <div className="application-notes-section">
                        <div className="application-notes-title">
                            Application Notes (Optional)
                        </div>
                        <div className="application-notes-description">
                            You can add any additional notes or comments to support your application.
                        </div>
                        <Input.TextArea
                            placeholder="Enter any additional notes or comments for your application..."
                            value={applicationNotes}
                            onChange={(e) => setApplicationNotes(e.target.value)}
                            rows={4}
                            maxLength={1000}
                            showCount
                            className="application-notes-textarea"
                        />
                    </div>
                )}

                {/* Modal Footer */}
                <div className="apply-modal-footer">
                    {validationError ? (
                        // Show only centered OK button when there's a validation error
                        <div className="modal-footer-centered">
                            <Button onClick={onCancel}>
                                OK
                            </Button>
                        </div>
                    ) : (
                        // Show normal buttons when no validation error
                        <div className="modal-footer-normal">
                           
                            <Button onClick={onCancel} disabled={isValidating}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                disabled={!canSubmit() || isValidating || isSubmitting || submitting}
                                loading={isSubmitting || submitting}
                            >
                                {isValidating ? 'Validating...' : 'Submit Application'}
                            </Button>
                        </div>
                    )}
                </div>
            </Modal>
            
            {/* Success Modal */}
            <SuccessModal
                visible={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    setSubmittedApplicationId(undefined);
                    // Main modal is already closed, just close success modal
                }}
                onSubmit={() => {
                    setShowSuccessModal(false);
                    setSubmittedApplicationId(undefined);
                    onSubmit(submittedApplicationId); // Call the parent's onSubmit handler
                }}
            />
        </>
    );
    } catch (error: any) {
        handleUnexpectedError(error, 'component rendering');
        return (
            <Modal
                open={visible}
                onCancel={onCancel}
                footer={null}
                title="Error"
                centered
                width={500}
            >
                <div className="error-dialog-content">
                    <p>An unexpected error occurred while rendering the modal.</p>
                    <p>Please try again or contact support if the problem persists.</p>
                    <Button onClick={onCancel} type="primary" className="error-dialog-button">
                        Close
                    </Button>
                </div>
            </Modal>
        );
    }
};

export default ApplyInternshipModal; 